import type { TourPage } from '@flobi/asset-library/tours';
import { createFile } from '../../home/api';
import { QUICK_SIZES, startSizeOf } from '../../home/sizes';

/**
 * Artwork's pages, as the kit's TourHost reads them (@flobi/asset-library/tours pages.js). The
 * editor's chapters need a file: the one this run made, else the one an earlier chapter
 * remembered (`filePath`), else a new "My first artwork", a square post.
 */
export const FILE_PAGE = /^\/f\/([^/?#]+)/;
export const FIRST_FILE_NAME = 'My first artwork';

const isFilePath = (path: unknown): path is string => typeof path === 'string' && FILE_PAGE.test(path);

/** Why the last try to make the file failed, for the paused pill (`failed`). */
let lastFailure: string | null = null;

/** The editor is up: its canvas and its dock are on the page (a viewer's file has no dock). */
const editorReady = () => typeof document !== 'undefined' && Boolean(document.querySelector('canvas.editor-canvas')) && Boolean(document.querySelector('[data-tour="artwork.dock"]'));

export const PAGES: Record<string, TourPage> = {
  home: { at: (p) => p === '/', go: () => '/' },
  file: {
    at: (p) => FILE_PAGE.test(p),
    ready: editorReady,
    opening: (vars) => (isFilePath(vars.filePath) ? null : ['Making your first artwork', 'Opening it']),
    failed: () => `Couldn’t make your first artwork${lastFailure ? `: ${lastFailure.replace(/\.$/, '')}` : ''}. Try again in a moment.`,
    open: async ({ vars, progress, setVars, save }) => {
      const known = [vars.filePath, progress.filePath].find(isFilePath);
      if (known) {
        if (vars.filePath !== known) setVars({ filePath: known });
        return known;
      }
      const square = QUICK_SIZES[0];
      try {
        const file = await createFile({ name: FIRST_FILE_NAME, ...(square ? { artboard: startSizeOf(square) } : {}) });
        const path = `/f/${file.id}`;
        setVars({ filePath: path });
        save((p) => ({ ...p, filePath: path }));
        return path;
      } catch (problem) {
        // Not made (a plan's file limit, the service down): the tour pauses and says why; Resume asks again.
        lastFailure = problem instanceof Error ? problem.message : null;
        return null;
      }
    },
  },
};
